import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./TenantTable.css";

const TenantTable = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/tenants/`);
      console.log("API Response:", res.data);
      setTenants(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This tenant will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/tenants/${id}`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Tenant has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchTenants();
    } catch (error) {
      console.error(error);

      Swal.fire(
        "Error",
        "Failed to delete tenant",
        "error"
      );
    }
  };

  const handleView = (tenant) => {
    navigate(`/superadmin/tenants/view-tenant/${tenant.id}`);
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      tenant.code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      tenant.contact_email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  /* Pagination */
  const totalEntries = filteredTenants.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const startIndex =
    totalEntries === 0
      ? 0
      : (currentPage - 1) * entriesPerPage;

  const endIndex = Math.min(
    startIndex + entriesPerPage,
    totalEntries
  );

  const currentTenants = filteredTenants.slice(
    startIndex,
    endIndex
  );

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-table-page-content">
          <div className="tenant-table-header">
            <div className="tenant-table-header-left">
              <h2>Tenants Management</h2>

              <span className="tenant-table-count">
                {tenants.length} tenants
              </span>
            </div>

            <div className="tenant-table-header-right">
              <div className="tenant-table-search-wrapper">
                <FiSearch className="tenant-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="tenant-table-search-input"
                />
              </div>

              <button
                className="tenant-table-refresh-btn"
                onClick={fetchTenants}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="tenant-table-loader-container">
              <div className="tenant-table-loader">
                <div className="tenant-table-spinner"></div>
                <p>Loading tenants...</p>
              </div>
            </div>
          ) : (
            <div className="tenant-table-container">
              <table className="tenant-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Currency</th>
                    <th>Status</th>
                    <th className="tenant-table-actions-header">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentTenants.length > 0 ? (
                    currentTenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td className="tenant-table-name">
                          <div className="tenant-table-name-cell">
                            <div className="tenant-table-avatar">
                              {tenant.name
                                ?.charAt(0)
                                .toUpperCase()}
                            </div>

                            <span>{tenant.name}</span>
                          </div>
                        </td>

                        <td>
                          <span className="tenant-table-code-badge">
                            {tenant.code}
                          </span>
                        </td>

                        <td>
                          {tenant.contact_email || "-"}
                        </td>

                        <td>
                          {tenant.contact_phone || "-"}
                        </td>

                        <td>
                          <span className="tenant-table-currency-text">
                            {tenant.default_currency || "-"}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`tenant-table-status ${
                              tenant.status === "ACTIVE"
                                ? "tenant-table-status-active"
                                : "tenant-table-status-inactive"
                            }`}
                          >
                            <span className="tenant-table-status-dot"></span>
                            {tenant.status}
                          </span>
                        </td>

                        <td className="tenant-table-actions-cell">
                          <button
                            className="tenant-table-action-btn tenant-table-view-btn"
                            onClick={() => handleView(tenant)}
                            title="View"
                          >
                            <FiEye />
                          </button>

                          <button
                            className="tenant-table-action-btn tenant-table-delete-btn"
                            onClick={() =>
                              handleDelete(tenant.id)
                            }
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="tenant-table-no-data"
                      >
                        <div className="tenant-table-empty-state">
                          <div className="tenant-table-empty-icon">
                            <FiSearch />
                          </div>

                          <p>No tenants found</p>

                          <span className="tenant-table-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "No tenants are available"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="tenant-table-pagination">
                <div className="tenant-table-pagination-left">
                  <span>Show</span>

                  <select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    className="tenant-table-entries-select"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>

                  <span>entries</span>
                </div>

                <div className="tenant-table-pagination-right">
                  <span className="tenant-table-showing-text">
                    Showing{" "}
                    <strong>
                      {totalEntries === 0
                        ? 0
                        : startIndex + 1}
                    </strong>{" "}
                    to{" "}
                    <strong>{endIndex}</strong>{" "}
                    of{" "}
                    <strong>{totalEntries}</strong>{" "}
                    entries
                  </span>

                  <div className="tenant-table-pagination-controls">
                    <button
                      className="tenant-table-pagination-btn"
                      onClick={() =>
                        goToPage(currentPage - 1)
                      }
                      disabled={currentPage === 1}
                      title="Previous"
                    >
                      <FiChevronLeft />
                    </button>

                    {getPageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span
                          key={`dots-${index}`}
                          className="tenant-table-pagination-dots"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          className={`tenant-table-pagination-btn ${
                            currentPage === page
                              ? "tenant-table-pagination-active"
                              : ""
                          }`}
                          onClick={() => goToPage(page)}
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      className="tenant-table-pagination-btn"
                      onClick={() =>
                        goToPage(currentPage + 1)
                      }
                      disabled={
                        currentPage === totalPages ||
                        totalPages === 0
                      }
                      title="Next"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantTable;